import { ref, useRoute, watch } from '@nuxtjs/composition-api';
import { useStore } from '~/composables/useStore';
import { PageData } from '~/store/internals/keepScroll';

export const useKeepScroll = () => {
  const store = useStore();
  const route = useRoute();
  const watchedRoute = ref(route.value);
  const isScrolled = ref<boolean>(false);

  /**
   * 関連リンクの開閉状態を復元する
   */
  const openDetailsElements = () => {
    const detailsElements = store.getters['internals/keepScroll/detailsElements'](
      watchedRoute.value.path,
      watchedRoute.value.fullPath
    );
    if (detailsElements?.length) {
      detailsElements.forEach((detailsElement) => {
        if (!detailsElement?.elementId) return;

        const detailsElementRef: HTMLDetailsElement | null = document.querySelector(`#${detailsElement.elementId}`);
        if (detailsElementRef) {
          detailsElementRef.open = detailsElement?.isOpen ?? false;
        }
      });
    }
  };

  /**
   * ハッシュのIDの要素へスクロールする
   */
  const scrollToHash = async (waitingTime?: number) => {
    if (isScrolled.value) return;

    const hash = watchedRoute.value.hash;
    if (hash) {
      const target = document.querySelector(decodeURIComponent(route.value.hash));
      if (waitingTime) {
        await new Promise((resolve) => setTimeout(resolve, waitingTime));
      }
      target?.scrollIntoView();
    }

    isScrolled.value = true;
  };

  const scrollToPreviousPosition = async (waitingTime?: number) => {
    if (isScrolled.value) return;

    openDetailsElements();

    const scrollPosition: number = store.getters['internals/keepScroll/scrollY'](
      watchedRoute.value.path,
      watchedRoute.value.fullPath
    );
    if (scrollPosition > 0) {
      if (waitingTime) {
        await new Promise((resolve) => setTimeout(resolve, waitingTime));
      }
      window.scrollTo({ top: scrollPosition });
    }

    isScrolled.value = true;
  };

  /**
   * スクロール位置を含めたデータをストアに格納する
   * * onUnmounted時に使用することを推奨
   *   * モーダルを表示している状態から遷移すると、背景を固定にしている影響でスクロール位置が0と取得されてしまうため
   *   * ページのアンマウントは、モーダルのアンマウントより後に発火するため、正しいスクロール位置が取得できる
   */
  const setPageData = (pageData: Omit<PageData, 'path' | 'fullPath' | 'scrollY'>) => {
    if (pageData.detailsElements?.length) {
      // 関連リンクの開閉状態を指定されたIDの<details/>から取得
      pageData.detailsElements = pageData.detailsElements.map((detailsElement) => {
        const detailsElementRef: HTMLDetailsElement | null = document.querySelector(`#${detailsElement.elementId}`);
        return {
          isOpen: detailsElement.isOpen ?? detailsElementRef?.hasAttribute('open') ?? false,
          elementId: detailsElement.elementId,
        };
      });
    }

    store.commit('internals/keepScroll/setPageData', {
      path: watchedRoute.value.path,
      fullPath: watchedRoute.value.fullPath,
      scrollY: window.scrollY,
      ...pageData,
    });
  };

  /**
   * ストアに保持されたパスのデータを削除する
   */
  const resetPageData = (path: string) => {
    store.commit('internals/keepScroll/resetPageData', path);
  };

  /**
   * 遷移前のrouteを保持するためにwatch
   * スクロール位置をセットするアンマウント時は、routeが遷移先のものに変わってしまうため
   */
  watch(
    () => route.value,
    () => {
      watchedRoute.value = route.value;
    }
  );

  return {
    scrollToHash,
    scrollToPreviousPosition,
    setPageData,
    resetPageData,
  };
};
